import React, { useState, useEffect, useRef, useCallback } from "react";

const API = (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1")
  ? "http://localhost:8001"
  : "https://burn-firsttimer-bot-production.up.railway.app";

const CHANNEL_ICONS = {
  email: "📧", whatsapp: "💬", instagram: "📸",
  tiktok: "🎵", facebook: "📘", webchat: "🌐", internal: "🔒"
};
const CHANNEL_LABELS = {
  email: "E-Mail", whatsapp: "WhatsApp", instagram: "Instagram",
  tiktok: "TikTok", facebook: "Facebook", webchat: "Web-Chat", internal: "Intern"
};
const STATUS_COLORS = {
  new: "#ef4444", in_progress: "#f59e0b", done: "#10b981"
};
const STATUS_LABELS = { new: "Neu", in_progress: "In Bearbeitung", done: "Erledigt" };
const PRIORITY_COLORS = { urgent: "#ef4444", normal: "#6b7280" };

// ─── Web-Chat Widget Builder ──────────────────────────────────────────────────
function WebchatBuilder({ settings, setSettings, baseUrl, notify }) {
  const [copied, setCopied] = useState(false);

  const color   = settings.webchat_color   || "#6366f1";
  const title   = settings.webchat_title   || "Wie können wir helfen?";
  const name    = settings.webchat_studio_name || "";
  const welcome = settings.webchat_welcome || "Hallo! 👋 Schreib uns, wir melden uns schnellstmöglich.";
  const logo    = settings.webchat_logo_url || "";
  const pos     = settings.webchat_position || "right";
  const studio  = settings.webchat_studio_id || "burn";

  // Build snippet dynamically
  const snippet = `<script>
  window.BURNWIDGET = {
    studio: "${studio}",
    color:  "${color}",
    title:  "${title}",
    name:   "${name}",
    welcome:"${welcome}",
    logo:   "${logo}",
    pos:    "${pos}"
  };
</script>
<script src="${baseUrl}/kommunikation/webchat/widget.js" async></script>`;

  const copySnippet = () => {
    navigator.clipboard.writeText(snippet).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
      notify("✅ Code-Snippet kopiert!");
    });
  };

  const F = ({ label, k, placeholder = "", hint = null, type = "text" }) => (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 3 }}>{label}</div>
      {hint && <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 4 }}>{hint}</div>}
      <input type={type} value={settings[k] || ""} placeholder={placeholder}
        onChange={e => setSettings(s => ({ ...s, [k]: e.target.value }))}
        style={{ width: "100%", padding: "8px 12px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 13, boxSizing: "border-box", outline: "none" }}
      />
    </div>
  );

  const T = ({ label, k, hint }) => (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontSize: 13, fontWeight: 500 }}>{label}</span>
        <button onClick={() => setSettings(s => ({ ...s, [k]: !s[k] }))} style={{
          width: 40, height: 22, borderRadius: 11, border: "none", cursor: "pointer",
          background: settings[k] ? color : "#d1d5db", position: "relative", transition: "background 0.2s", flexShrink: 0
        }}>
          <div style={{ width: 18, height: 18, borderRadius: "50%", background: "#fff", position: "absolute", top: 2, left: settings[k] ? 20 : 2, transition: "left 0.2s" }} />
        </button>
      </div>
      {hint && <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>{hint}</div>}
    </div>
  );

  return (
    <div>
      {/* Two-column layout: fields left, preview right */}
      <div style={{ display: "flex", gap: 20, marginBottom: 20 }}>

        {/* Left: fields */}
        <div style={{ flex: 1 }}>
          <F label="Studio-Name (wird im Widget angezeigt)" k="webchat_studio_name" placeholder="BURN Boxing Studio"
            hint="Name der oberhalb des Chat-Titels steht." />
          <F label="Chat-Titel" k="webchat_title" placeholder="Wie können wir helfen?"
            hint="Hauptüberschrift im Chat-Fenster." />
          <F label="Begrüßungsnachricht" k="webchat_welcome" placeholder="Hallo! 👋 Schreib uns..."
            hint="Erste Nachricht die Besucher sehen wenn sie das Chat-Fenster öffnen." />
          <F label="Logo-URL" k="webchat_logo_url" placeholder="https://deinewebsite.com/logo.png"
            hint="Direkt-Link zu deinem Logo (PNG/JPG, am besten quadratisch). Leer lassen = kein Logo." />

          <div style={{ display: "flex", gap: 12, marginBottom: 14 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 3 }}>Primärfarbe</div>
              <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 4 }}>Button- und Header-Farbe</div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input type="color" value={color}
                  onChange={e => setSettings(s => ({ ...s, webchat_color: e.target.value }))}
                  style={{ width: 40, height: 36, border: "none", borderRadius: 8, cursor: "pointer", padding: 2 }}
                />
                <input type="text" value={color}
                  onChange={e => setSettings(s => ({ ...s, webchat_color: e.target.value }))}
                  style={{ flex: 1, padding: "8px 10px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 13, outline: "none" }}
                />
              </div>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 3 }}>Position</div>
              <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 4 }}>Wo erscheint der Button?</div>
              <select value={pos} onChange={e => setSettings(s => ({ ...s, webchat_position: e.target.value }))}
                style={{ width: "100%", padding: "8px 12px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 13, background: "#fff", outline: "none" }}>
                <option value="right">Unten rechts</option>
                <option value="left">Unten links</option>
              </select>
            </div>
          </div>

          <F label="Studio-ID (technisch)" k="webchat_studio_id" placeholder="burn"
            hint="Interne ID — nur ändern beim White-Label für andere Studios. Standard: burn" />

          <T label="Web-Chat aktiviert" k="webchat_enabled" />
          <T label="KI antwortet automatisch" k="webchat_ai_auto"
            hint="KI beantwortet eingehende Web-Chat-Nachrichten ohne manuelle Bestätigung." />
        </div>

        {/* Right: live preview */}
        <div style={{ width: 200, flexShrink: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 8, textAlign: "center" }}>👁️ Live-Vorschau</div>
          <div style={{ position: "relative", height: 320, background: "#f3f4f6", borderRadius: 12, border: "1px solid #e5e7eb", overflow: "hidden" }}>
            {/* Simulated website bg */}
            <div style={{ padding: 12 }}>
              <div style={{ height: 8, background: "#d1d5db", borderRadius: 4, marginBottom: 6, width: "80%" }} />
              <div style={{ height: 8, background: "#d1d5db", borderRadius: 4, marginBottom: 6, width: "60%" }} />
              <div style={{ height: 8, background: "#d1d5db", borderRadius: 4, width: "70%" }} />
            </div>
            {/* Chat bubble button */}
            <div style={{
              position: "absolute", bottom: 12, [pos === "left" ? "left" : "right"]: 12,
              width: 44, height: 44, borderRadius: "50%", background: color,
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: "0 4px 12px rgba(0,0,0,0.2)", cursor: "pointer", fontSize: 20
            }}>💬</div>
            {/* Chat window preview */}
            <div style={{
              position: "absolute", bottom: 64, [pos === "left" ? "left" : "right"]: 8,
              width: 170, background: "#fff", borderRadius: 12, boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
              overflow: "hidden", border: "1px solid #e5e7eb"
            }}>
              {/* Header */}
              <div style={{ background: color, padding: "10px 12px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {logo ? (
                    <img src={logo} alt="" style={{ width: 24, height: 24, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} onError={e => e.target.style.display = "none"} />
                  ) : (
                    <div style={{ width: 24, height: 24, borderRadius: "50%", background: "rgba(255,255,255,0.3)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12 }}>💬</div>
                  )}
                  <div>
                    {name && <div style={{ fontSize: 9, color: "rgba(255,255,255,0.8)", lineHeight: 1 }}>{name}</div>}
                    <div style={{ fontSize: 11, color: "#fff", fontWeight: 700, lineHeight: 1.2 }}>{title || "Chat"}</div>
                  </div>
                </div>
              </div>
              {/* Welcome message */}
              <div style={{ padding: 10 }}>
                <div style={{ background: "#f3f4f6", borderRadius: "12px 12px 12px 4px", padding: "6px 8px", fontSize: 10, color: "#374151", lineHeight: 1.4 }}>
                  {welcome.length > 60 ? welcome.slice(0, 57) + "..." : welcome}
                </div>
                <div style={{ marginTop: 8, display: "flex", gap: 4 }}>
                  <input readOnly placeholder="Nachricht..." style={{ flex: 1, padding: "4px 6px", border: "1px solid #e5e7eb", borderRadius: 6, fontSize: 9, background: "#f9fafb" }} />
                  <div style={{ width: 22, height: 22, borderRadius: "50%", background: color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: "#fff" }}>↗</div>
                </div>
              </div>
            </div>
          </div>
          <div style={{ fontSize: 10, color: "#9ca3af", textAlign: "center", marginTop: 6 }}>Echte Vorschau deines Widgets</div>
        </div>
      </div>

      {/* Code snippet */}
      <div style={{ background: "#1e1e2e", borderRadius: 12, overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#a6e3a1" }}>📋 Code-Snippet — vor &lt;/body&gt; auf deine Website kopieren</div>
          <button onClick={copySnippet} style={{
            padding: "5px 14px", borderRadius: 6, border: "none", cursor: "pointer",
            background: copied ? "#a6e3a1" : color, color: copied ? "#1e1e2e" : "#fff",
            fontSize: 12, fontWeight: 700, transition: "all 0.2s"
          }}>
            {copied ? "✓ Kopiert!" : "Kopieren"}
          </button>
        </div>
        <pre style={{ margin: 0, padding: "14px 16px", fontSize: 11, color: "#cdd6f4", overflowX: "auto", lineHeight: 1.7, fontFamily: "monospace" }}>
          {snippet}
        </pre>
      </div>
      <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 8 }}>
        💡 Einstellungen erst speichern → dann Snippet kopieren. Bei Änderungen (z.B. neue Farbe) Snippet erneut kopieren und auf der Website ersetzen.
      </div>
    </div>
  );
}

function parseUtc(iso) {
  if (!iso) return new Date(0);
  // Append Z if no timezone info so browser treats as UTC
  return new Date(iso.endsWith("Z") || iso.includes("+") ? iso : iso + "Z");
}
function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - parseUtc(iso).getTime()) / 1000;
  if (diff < 60) return "Gerade eben";
  if (diff < 3600) return `${Math.floor(diff / 60)} Min`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} Std`;
  return parseUtc(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" });
}

// ─── Email quote splitter ────────────────────────────────────────────────────
function splitEmailQuote(body) {
  if (!body) return { main: "", quote: "" };
  const lines = body.split("\n");
  // Common quote starters: lines with >, "schrieb am", "wrote:", "On ... wrote:"
  const quoteStart = lines.findIndex((l, i) => {
    const t = l.trim();
    return (
      (t.startsWith(">") && i > 0) ||
      /schrieb am .{5,}/i.test(t) ||
      /wrote on .{5,}/i.test(t) ||
      /^-{3,}.*original message/i.test(t) ||
      /^_{3,}/.test(t) ||
      t === "--" || t === "—" || t === "––"
    );
  });
  if (quoteStart <= 0) return { main: body.trim(), quote: "" };
  // Also include the line before if it's a "Name schrieb..." intro line
  const cutAt = (quoteStart > 0 && /schrieb|wrote/i.test(lines[quoteStart - 1]))
    ? quoteStart - 1 : quoteStart;
  return {
    main: lines.slice(0, cutAt).join("\n").trim(),
    quote: lines.slice(cutAt).join("\n").trim()
  };
}

function EmailBody({ body, isHtml }) {
  const [showQuote, setShowQuote] = useState(false);
  if (isHtml) return <div dangerouslySetInnerHTML={{ __html: body }} style={{ wordBreak: "break-word" }} />;
  const { main, quote } = splitEmailQuote(body);
  return (
    <div>
      <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{main || body}</div>
      {quote && (
        <div style={{ marginTop: 8 }}>
          <button onClick={() => setShowQuote(v => !v)} style={{
            background: "none", border: "1px solid #e5e7eb", borderRadius: 6,
            padding: "2px 10px", fontSize: 11, color: "#6b7280", cursor: "pointer"
          }}>
            {showQuote ? "▲ Zitat ausblenden" : "▼ Zitat anzeigen"}
          </button>
          {showQuote && (
            <div style={{
              marginTop: 8, padding: "10px 12px", background: "#f9fafb",
              borderLeft: "3px solid #d1d5db", borderRadius: "0 6px 6px 0",
              fontSize: 12, color: "#6b7280", whiteSpace: "pre-wrap", wordBreak: "break-word"
            }}>
              {quote}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Avatar({ name = "?" }) {
  const c = ["#6366f1","#8b5cf6","#ec4899","#f59e0b","#10b981","#3b82f6"][
    name.charCodeAt(0) % 6
  ];
  return (
    <div style={{
      width: 36, height: 36, borderRadius: "50%", background: c,
      display: "flex", alignItems: "center", justifyContent: "center",
      color: "#fff", fontWeight: 700, fontSize: 14, flexShrink: 0
    }}>{(name || "?")[0].toUpperCase()}</div>
  );
}

// ─── Einstellungen: module-level Field + Toggle (prevents input remount on keystroke) ─
function EinstellungenField({ label, k, type, placeholder, hint, settings, onChange }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 3 }}>{label}</div>
      {hint && <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 5, lineHeight: 1.5 }}>{hint}</div>}
      <input
        type={type || "text"}
        value={settings[k] || ""}
        placeholder={placeholder || ""}
        onChange={e => onChange(k, e.target.value)}
        style={{
          width: "100%", padding: "8px 12px", border: "1px solid #e5e7eb",
          borderRadius: 8, fontSize: 14, boxSizing: "border-box",
          background: (type || "text") === "password" ? "#fafafa" : "#fff", outline: "none"
        }}
      />
    </div>
  );
}
function EinstellungenToggle({ label, k, hint, settings, onChange }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontSize: 14, fontWeight: 500 }}>{label}</span>
        <button onClick={() => onChange(k, !settings[k])} style={{
          width: 44, height: 24, borderRadius: 12, border: "none", cursor: "pointer",
          background: settings[k] ? "#6366f1" : "#d1d5db", position: "relative", transition: "background 0.2s", flexShrink: 0
        }}>
          <div style={{
            width: 20, height: 20, borderRadius: "50%", background: "#fff",
            position: "absolute", top: 2, left: settings[k] ? 22 : 2, transition: "left 0.2s"
          }} />
        </button>
      </div>
      {hint && <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 3 }}>{hint}</div>}
    </div>
  );
}

// ─── Einstellungen Tab ────────────────────────────────────────────────────────
function EinstellungenTab({ notify }) {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch(`${API}/kommunikation/settings`)
      .then(r => r.json()).then(d => setSettings(d.settings || {})).catch(() => {});
  }, []);

  // ── handleChange must be before early return (Rules of Hooks) ──────────────
  const handleChange = useCallback((k, v) => setSettings(s => ({ ...s, [k]: v })), []);

  const save = async () => {
    setSaving(true);
    try {
      await fetch(`${API}/kommunikation/settings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ studio_id: "burn", settings })
      });
      notify("✅ Einstellungen gespeichert");
    } catch { notify("❌ Fehler beim Speichern"); }
    setSaving(false);
  };

  if (!settings) return <div style={{ padding: 32, color: "#6b7280" }}>Lade...</div>;

  const Guide = ({ steps, color = "#eff6ff", borderColor = "#bfdbfe", textColor = "#1e40af" }) => (
    <div style={{ background: color, border: `1px solid ${borderColor}`, borderRadius: 8, padding: "12px 14px", marginBottom: 16 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: textColor, marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.5 }}>📋 Schritt-für-Schritt Anleitung</div>
      <ol style={{ margin: 0, paddingLeft: 18, display: "flex", flexDirection: "column", gap: 5 }}>
        {steps.map((s, i) => (
          <li key={i} style={{ fontSize: 12, color: "#374151", lineHeight: 1.6 }}
            dangerouslySetInnerHTML={{ __html: s }} />
        ))}
      </ol>
    </div>
  );

  const Link = ({ href, label }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: "#6366f1", fontWeight: 600, textDecoration: "none" }}>
      🔗 {label} ↗
    </a>
  );

  const WebhookBox = ({ url, label }) => (
    <div style={{ background: "#f3f4f6", borderRadius: 8, padding: "10px 14px", marginBottom: 16 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", marginBottom: 4, textTransform: "uppercase" }}>📡 Deine Webhook-URL ({label})</div>
      <div style={{ fontFamily: "monospace", fontSize: 12, color: "#111827", wordBreak: "break-all", background: "#fff", padding: "6px 10px", borderRadius: 6, border: "1px solid #e5e7eb" }}>
        {url}
      </div>
      <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 5 }}>Diese URL trägst du bei Meta/TikTok als Webhook-URL ein.</div>
    </div>
  );

  const baseUrl = "https://burn-firsttimer-bot-production.up.railway.app";

  return (
    <div style={{ padding: 24, maxWidth: 680, overflowY: "auto", height: "100%", boxSizing: "border-box" }}>
      <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 4, color: "#111827" }}>⚙️ Kanal-Einstellungen</h2>
      <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 28 }}>
        Alle Zugangsdaten werden <strong>verschlüsselt</strong> gespeichert (AES-256). Kein Klartext auf dem Server.
      </p>

      {/* ── E-Mail ── */}
      <Section title="📧 E-Mail (IMAP / SMTP)">
        <Guide steps={[
          'Gehe zu deinem E-Mail-Anbieter (z.B. <strong>Gmail, Outlook, eigener Server</strong>).',
          '<strong>Gmail:</strong> Aktiviere "2-Faktor-Authentifizierung" → dann unter <a href="https://myaccount.google.com/apppasswords" target="_blank" style="color:#6366f1">myaccount.google.com/apppasswords</a> ein App-Passwort erstellen → Kategorie "Mail" wählen → 16-stelligen Code kopieren.',
          '<strong>Outlook / Office 365:</strong> IMAP Host: <code>outlook.office365.com</code>, Port 993. SMTP: <code>smtp.office365.com</code>, Port 587.',
          '<strong>Eigene Domain (z.B. info@burnbxng.com):</strong> IMAP/SMTP Daten beim Hoster (Strato, IONOS, Hetzner etc.) unter "E-Mail → Postfach-Einstellungen" finden.',
          'Alle Felder ausfüllen → Speichern → fertig. E-Mails werden automatisch abgerufen.',
        ]} />
        <EinstellungenField settings={settings} onChange={handleChange} label="IMAP Host" k="email_imap_host" placeholder="imap.gmail.com"
          hint="Für Gmail: imap.gmail.com | Outlook: outlook.office365.com | Ionos: imap.ionos.de" />
        <EinstellungenField settings={settings} onChange={handleChange} label="IMAP Port" k="email_imap_port" placeholder="993"
          hint="Standard: 993 (SSL). Nur ändern wenn dein Anbieter einen anderen Port verlangt." />
        <EinstellungenField settings={settings} onChange={handleChange} label="SMTP Host" k="email_smtp_host" placeholder="smtp.gmail.com"
          hint="Für Gmail: smtp.gmail.com | Outlook: smtp.office365.com | Ionos: smtp.ionos.de" />
        <EinstellungenField settings={settings} onChange={handleChange} label="SMTP Port" k="email_smtp_port" placeholder="587"
          hint="Standard: 587 (STARTTLS). Alternative: 465 (SSL)." />
        <EinstellungenField settings={settings} onChange={handleChange} label="E-Mail Adresse" k="email_user" placeholder="info@meinestudio.com"
          hint="Die E-Mail-Adresse mit der du dich beim Postfach anmeldest." />
        <EinstellungenField settings={settings} onChange={handleChange} label="Passwort / App-Passwort" k="email_password" type="password"
          hint="Bei Gmail: das 16-stellige App-Passwort verwenden (NICHT dein Google-Passwort). Wird verschlüsselt gespeichert." />
        <EinstellungenField settings={settings} onChange={handleChange} label="Abruf-Intervall (Sekunden)" k="email_poll_interval_seconds" placeholder="60"
          hint="Wie oft neue E-Mails automatisch abgerufen werden. Minimum: 30 Sek. Empfehlung: 60 Sek." />
        <EinstellungenToggle settings={settings} onChange={handleChange} label="E-Mail Kanal aktiviert" k="email_enabled" />
        <EinstellungenToggle settings={settings} onChange={handleChange} label="KI antwortet automatisch" k="email_ai_auto"
          hint="KI sendet Antworten ohne Bestätigung. Empfehlung: erst deaktiviert lassen und KI-Vorschläge manuell prüfen." />
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 3 }}>✍️ E-Mail Signatur</div>
          <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 4 }}>Wird automatisch an jede ausgehende E-Mail angehängt.</div>
          <textarea
            value={settings.email_signature || ""}
            onChange={e => handleChange("email_signature", e.target.value)}
            rows={5}
            placeholder={"Herzliche Grüße\nDein BURN Team\n\nBURN Boxing Studio\nhello@burnbxng.com\nwww.burnbxng.com"}
            style={{ width: "100%", padding: "10px 12px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 13, boxSizing: "border-box", outline: "none", resize: "vertical", fontFamily: "inherit", lineHeight: 1.5 }}
          />
        </div>
      </Section>

      {/* ── WhatsApp ── */}
      <Section title="💬 WhatsApp (Meta Cloud API)">
        <Guide color="#f0fdf4" borderColor="#bbf7d0" textColor="#166534" steps={[
          'Gehe zu <a href="https://developers.facebook.com" target="_blank" style="color:#6366f1">developers.facebook.com</a> → "Meine Apps" → App auswählen (oder neue App erstellen → Typ: "Business").',
          'Im linken Menü: <strong>WhatsApp → Einrichtung</strong>. Dort siehst du direkt die <strong>Phone Number ID</strong> (eine lange Zahl).',
          '<strong>Access Token:</strong> Unter "WhatsApp → API-Einrichtung" → "Temporäres Zugriffstoken" kopieren. Für dauerhaften Betrieb: System-Benutzer-Token über den Business Manager erstellen.',
          '<strong>Verify Token:</strong> Erfinde dir ein beliebiges Passwort (z.B. <code>meinStudio2026</code>) — das trägst du hier UND bei Meta ein.',
          'Webhook-URL (unten) bei Meta unter "WhatsApp → Konfiguration → Webhook" eintragen. Felder abonnieren: <strong>messages</strong>.',
        ]} />
        <WebhookBox url={`${baseUrl}/kommunikation/whatsapp/webhook`} label="WhatsApp" />
        <EinstellungenField settings={settings} onChange={handleChange} label="Phone Number ID" k="whatsapp_phone_number_id"
          hint="Zu finden unter: Meta Developers → Deine App → WhatsApp → API-Einrichtung → 'Phone Number ID' (15–16-stellige Zahl)." />
        <EinstellungenField settings={settings} onChange={handleChange} label="Access Token" k="whatsapp_token" type="password"
          hint="Temporäres Token (24h gültig) oder dauerhafter System-Benutzer-Token. Unter: Meta Developers → App → WhatsApp → API-Einrichtung." />
        <EinstellungenField settings={settings} onChange={handleChange} label="Verify Token (selbst wählen)" k="whatsapp_verify_token"
          hint="Beliebiger Text den du dir selbst ausdenkst (z.B. 'MeinStudio2026'). Wird bei Meta als Webhook-Verify-Token eingetragen." />
        <EinstellungenToggle settings={settings} onChange={handleChange} label="WhatsApp Kanal aktiviert" k="whatsapp_enabled" />
        <EinstellungenToggle settings={settings} onChange={handleChange} label="KI antwortet automatisch" k="whatsapp_ai_auto"
          hint="KI sendet WhatsApp-Antworten automatisch. Nur aktivieren wenn KI-Vorschläge geprüft und gut sind." />
      </Section>

      {/* ── Instagram ── */}
      <Section title="📸 Instagram">
        <Guide color="#fdf4ff" borderColor="#e9d5ff" textColor="#6b21a8" steps={[
          'Instagram muss mit einer <strong>Facebook Page</strong> verbunden sein (Instagram Business Account).',
          'Gehe zu <a href="https://developers.facebook.com" target="_blank" style="color:#6366f1">developers.facebook.com</a> → Deine App → <strong>Instagram → API-Einrichtung</strong>.',
          'Unter "Zugriffstoken" → Token für dein Instagram-Konto generieren → kopieren.',
          'Webhook-URL (unten) unter "Instagram → Webhooks" eintragen. Felder: <strong>messages, comments</strong> abonnieren.',
          '<strong>Wichtig:</strong> Dein App muss den "instagram_basic" und "instagram_manage_messages" Permission haben (Live-Modus).',
        ]} />
        <WebhookBox url={`${baseUrl}/kommunikation/instagram/webhook`} label="Instagram" />
        <EinstellungenField settings={settings} onChange={handleChange} label="Access Token" k="instagram_token" type="password"
          hint="Zu finden unter: Meta Developers → Deine App → Instagram → API-Einrichtung → Zugriffstoken. Muss instagram_manage_messages Permission haben." />
        <EinstellungenField settings={settings} onChange={handleChange} label="Verify Token (selbst wählen)" k="instagram_verify_token"
          hint="Beliebiger Text den du dir selbst ausdenkst (z.B. 'MeinStudio2026'). Wird bei Meta → Instagram → Webhooks als Verify Token eingetragen." />
        <EinstellungenToggle settings={settings} onChange={handleChange} label="Instagram Kanal aktiviert" k="instagram_enabled" />
        <EinstellungenToggle settings={settings} onChange={handleChange} label="KI antwortet automatisch" k="instagram_ai_auto" />
      </Section>

      {/* ── Facebook ── */}
      <Section title="📘 Facebook">
        <Guide color="#eff6ff" borderColor="#bfdbfe" textColor="#1e40af" steps={[
          'Gehe zu <a href="https://developers.facebook.com" target="_blank" style="color:#6366f1">developers.facebook.com</a> → Deine App → <strong>Messenger → Einrichtung</strong>.',
          'Unter "Zugriffstoken" → deine Facebook Page auswählen → Token generieren → kopieren.',
          '<strong>Verify Token:</strong> Beliebigen Text selbst wählen (gleiche Logik wie bei WhatsApp).',
          'Webhook-URL (unten) unter "Messenger → Webhooks" eintragen. Felder: <strong>messages, messaging_postbacks</strong>.',
          'Permissions: <strong>pages_messaging</strong> muss in deiner App aktiv sein (Live-Modus).',
        ]} />
        <WebhookBox url={`${baseUrl}/kommunikation/facebook/webhook`} label="Facebook" />
        <EinstellungenField settings={settings} onChange={handleChange} label="Page Access Token" k="facebook_token" type="password"
          hint="Unter: Meta Developers → App → Messenger → Einrichtung → Zugriffstoken für deine Page. NICHT dein persönliches Facebook-Token." />
        <EinstellungenField settings={settings} onChange={handleChange} label="Verify Token (selbst wählen)" k="facebook_verify_token"
          hint="Beliebiger Text — identisch mit dem was du bei Meta als Webhook-Verify-Token einträgst." />
        <EinstellungenToggle settings={settings} onChange={handleChange} label="Facebook Kanal aktiviert" k="facebook_enabled" />
        <EinstellungenToggle settings={settings} onChange={handleChange} label="KI antwortet automatisch" k="facebook_ai_auto" />
      </Section>

      {/* ── TikTok ── */}
      <Section title="🎵 TikTok (nur Kommentare)">
        <div style={{ background: "#fef9c3", border: "1px solid #fde68a", borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 12, color: "#78350f" }}>
          ⚠️ <strong>Hinweis:</strong> TikTok erlaubt über die offizielle API nur das Lesen und Beantworten von <strong>Kommentaren</strong> — keine Direktnachrichten (DMs). Das ist eine TikTok-Einschränkung, keine technische Lücke.
        </div>
        <Guide color="#fff7ed" borderColor="#fed7aa" textColor="#9a3412" steps={[
          'Gehe zu <a href="https://developers.tiktok.com" target="_blank" style="color:#6366f1">developers.tiktok.com</a> → "Manage Apps" → App erstellen oder auswählen.',
          'Unter <strong>Products → Comment API</strong> → App für Kommentar-Zugriff aktivieren.',
          'Access Token generieren (OAuth-Flow) → Token kopieren.',
          'Webhook-URL (unten) im TikTok Developer Portal als Callback-URL eintragen.',
          '<strong>Wichtig:</strong> TikTok-App muss verifiziert/genehmigt sein für Comment API-Zugriff (kann einige Tage dauern).',
        ]} />
        <WebhookBox url={`${baseUrl}/kommunikation/tiktok/webhook`} label="TikTok" />
        <EinstellungenField settings={settings} onChange={handleChange} label="Access Token" k="tiktok_token" type="password"
          hint="Unter: TikTok Developer Portal → Deine App → Keys and Token → Access Token. Nur für Kommentar-API." />
        <EinstellungenToggle settings={settings} onChange={handleChange} label="TikTok Kanal aktiviert" k="tiktok_enabled" />
        <EinstellungenToggle settings={settings} onChange={handleChange} label="KI antwortet automatisch" k="tiktok_ai_auto" />
      </Section>

      {/* ── Web-Chat ── */}
      <Section title="🌐 Web-Chat Widget">
        <WebchatBuilder settings={settings} setSettings={setSettings} baseUrl={baseUrl} notify={notify} />
      </Section>

      {/* ── KI Global ── */}
      {/* ── KI API Keys ── */}
      <Section title="🤖 KI — API Keys (eigene Zugangsdaten)">
        <div style={{ background: "#fef9c3", border: "1px solid #fde68a", borderRadius: 8, padding: "12px 14px", marginBottom: 16, fontSize: 12, color: "#78350f" }}>
          ⚠️ <strong>Wichtig:</strong> Die KI-Funktionen (Antwortvorschläge, Auto-Antwort, Übersetzung, Kategorisierung) funktionieren <strong>nur mit eigenen API Keys</strong>. Jeder Studio-Betreiber trägt seine eigenen Keys ein und bezahlt seine eigene Nutzung direkt beim Anbieter. Ohne Keys: KI-Features deaktiviert, alle anderen Kanäle laufen normal weiter.
        </div>

        <Guide color="#f5f3ff" borderColor="#ddd6fe" textColor="#5b21b6" steps={[
          '<strong>Anthropic (Claude) — empfohlen, Primär-KI:</strong> Gehe zu <a href="https://console.anthropic.com" target="_blank" style="color:#6366f1">console.anthropic.com</a> → Account erstellen → "API Keys" → "Create Key" → Key kopieren. Kosten: ~$0,001 pro Nachricht (sehr günstig).',
          '<strong>OpenAI (GPT) — optionaler Fallback:</strong> Gehe zu <a href="https://platform.openai.com/api-keys" target="_blank" style="color:#6366f1">platform.openai.com/api-keys</a> → "Create new secret key" → Key kopieren. Wird automatisch genutzt wenn Claude nicht antwortet.',
          'Beide Keys hier eintragen → Speichern → KI ist sofort aktiv.',
          'Kein Fallback-Key? Kein Problem — wenn Claude mal nicht erreichbar ist, bleibt das KI-Feld im Widget/CRM einfach leer. Das System läuft weiter.',
        ]} />

        <EinstellungenField settings={settings} onChange={handleChange} label="Anthropic API Key (Claude Haiku — Primär)" k="ai_anthropic_key" type="password"
          hint="Zu finden unter: console.anthropic.com → API Keys. Format: sk-ant-api03-... — wird verschlüsselt gespeichert." />

        <div style={{ background: "#f9fafb", borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 12, color: "#6b7280" }}>
          💡 <strong>Modell:</strong> Wir nutzen <strong>Claude Haiku</strong> — das schnellste und günstigste Claude-Modell. Ideal für kurze Antworten im Chat. Typische Kosten: &lt; 1 Cent pro 10 Nachrichten.
        </div>

        <EinstellungenField settings={settings} onChange={handleChange} label="OpenAI API Key (GPT-4o mini — Fallback, optional)" k="ai_openai_key" type="password"
          hint="Zu finden unter: platform.openai.com/api-keys. Format: sk-... — nur nötig als Absicherung falls Claude nicht antwortet." />

        <div style={{ background: "#f9fafb", borderRadius: 8, padding: "10px 14px", marginBottom: 16, fontSize: 12, color: "#6b7280" }}>
          🔄 <strong>Fallback-Logik:</strong> Claude antwortet nicht → GPT-4o mini wird automatisch versucht → beide nicht verfügbar → KI-Feld bleibt leer (kein Absturz, kein Fehler für den Nutzer).
        </div>

        <EinstellungenToggle settings={settings} onChange={handleChange} label="KI-Features global aktiviert" k="ai_enabled"
          hint="Wenn deaktiviert: keine KI-Vorschläge, keine automatischen Kategorisierungen — auch wenn API Keys hinterlegt sind." />
        <EinstellungenToggle settings={settings} onChange={handleChange} label="KI-Gedächtnis (Kontakt-Historie)" k="ai_memory_enabled"
          hint="KI merkt sich wichtige Infos pro Kontakt (Mitgliedschaft, letzte Anfragen) für bessere Antworten." />
        <EinstellungenToggle settings={settings} onChange={handleChange} label="Auto-Übersetzung" k="ai_auto_translate"
          hint="Eingehende Nachrichten in anderen Sprachen werden automatisch ins Deutsche übersetzt." />
        <EinstellungenToggle settings={settings} onChange={handleChange} label="Dringlichkeits-Erkennung" k="ai_urgency_detection"
          hint="KI erkennt automatisch dringende Nachrichten (z.B. Beschwerden, Kündigungen) und markiert sie rot." />
      </Section>

      <button onClick={save} disabled={saving} style={{
        background: "#6366f1", color: "#fff", border: "none", borderRadius: 8,
        padding: "12px 28px", fontSize: 14, fontWeight: 600, cursor: "pointer",
        marginTop: 8, opacity: saving ? 0.7 : 1, marginBottom: 40
      }}>
        {saving ? "Speichern..." : "💾 Einstellungen speichern"}
      </button>
    </div>
  );
}

function Section({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ marginBottom: 12 }}>
      <button onClick={() => setOpen(v => !v)} style={{
        width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "12px 16px", background: open ? "#ede9fe" : "#f3f4f6",
        border: "none", borderRadius: open ? "12px 12px 0 0" : 12,
        cursor: "pointer", textAlign: "left"
      }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: open ? "#6366f1" : "#374151", letterSpacing: 0.3 }}>{title}</span>
        <span style={{ fontSize: 16, color: open ? "#6366f1" : "#9ca3af", transition: "transform 0.2s", display: "inline-block", transform: open ? "rotate(180deg)" : "rotate(0deg)" }}>▾</span>
      </button>
      {open && (
        <div style={{ background: "#f9fafb", borderRadius: "0 0 12px 12px", padding: 16, borderTop: "1px solid #e5e7eb" }}>
          {children}
        </div>
      )}
    </div>
  );
}

// ─── Kontakt-Notizen (editierbar) ────────────────────────────────────────────
function ContactNotes({ contactId }) {
  const [notes, setNotes] = useState("");
  const [saved, setSaved] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!contactId) return;
    fetch(`${API}/kommunikation/contacts/${contactId}`)
      .then(r => r.json())
      .then(d => { setNotes(d.notes || ""); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, [contactId]);

  const save = async () => {
    await fetch(`${API}/kommunikation/contacts/${contactId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ notes })
    });
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  if (!contactId) return null;
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ color: "#6b7280", fontSize: 11, marginBottom: 4 }}>📝 Notizen zum Kontakt</div>
      <textarea
        value={notes}
        onChange={e => setNotes(e.target.value)}
        placeholder="Notizen zu diesem Kontakt..."
        rows={3}
        style={{
          width: "100%", padding: "8px 10px", border: "1px solid #e5e7eb",
          borderRadius: 8, fontSize: 12, boxSizing: "border-box", outline: "none",
          resize: "vertical", fontFamily: "inherit", lineHeight: 1.5
        }}
      />
      <button onClick={save} style={{
        marginTop: 4, padding: "4px 12px", background: saved ? "#10b981" : "#6366f1",
        color: "#fff", border: "none", borderRadius: 6, fontSize: 11,
        cursor: "pointer", fontWeight: 600, transition: "background 0.2s"
      }}>
        {saved ? "✓ Gespeichert" : "Speichern"}
      </button>
    </div>
  );
}

// ─── Haupt-Inbox ──────────────────────────────────────────────────────────────
function InboxView({ channel, notify, user }) {
  const [messages, setMessages] = useState([]);
  const [selected, setSelected] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchQ, setSearchQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [signature, setSignature] = useState("");
  const [thread, setThread] = useState([]);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const [internalNote, setInternalNote] = useState("");
  const [aiSuggestion, setAiSuggestion] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [activePane, setActivePane] = useState("reply"); // reply | internal
  const [contact, setContact] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [labels, setLabels] = useState([]);
  const [translating, setTranslating] = useState(false);
  const [summarizing, setSummarizing] = useState(false);
  const [summary, setSummary] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkMode, setBulkMode] = useState(false);
  const pollRef = useRef(null);
  const lastTs = useRef(new Date().toISOString());
  const threadEndRef = useRef(null);

  const fetchMessages = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (channel !== "all") params.set("channel", channel);
      if (filterStatus !== "all") params.set("status", filterStatus);
      const r = await fetch(`${API}/kommunikation/messages?${params}`);
      const d = await r.json();
      setMessages(d.messages || []);
    } catch {}
  }, [channel, filterStatus]);

  const fetchThread = useCallback(async (msgId) => {
    try {
      const r = await fetch(`${API}/kommunikation/messages/${msgId}/thread`);
      const d = await r.json();
      setThread(d.thread || d.messages || []);
      setTimeout(() => threadEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch {}
  }, []);

  const fetchAiSuggestion = useCallback(async (msg) => {
    if (!msg) return;
    setAiLoading(true);
    setAiSuggestion("");
    try {
      const r = await fetch(`${API}/kommunikation/ai/suggest`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message_id: msg.id, channel: msg.channel, message: msg.body, contact_name: msg.contact_name, studio_id: "burn" })
      });
      const d = await r.json();
      setAiSuggestion(d.suggestion || "");
    } catch {}
    setAiLoading(false);
  }, []);

  const toggleSelect = (id, e) => {
    e.stopPropagation();
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selectedIds.size === sorted.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(sorted.map(m => m.id)));
    }
  };

  const bulkAction = async (action, status) => {
    if (selectedIds.size === 0) return;
    const ids = [...selectedIds];
    try {
      const r = await fetch(`${API}/kommunikation/messages/bulk`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids, action, status })
      });
      const d = await r.json();
      if (d.ok) {
        if (action === "delete") {
          notify(`🗑️ ${d.deleted} Nachricht${d.deleted !== 1 ? "en" : ""} gelöscht`);
          if (selected && selectedIds.has(selected.id)) setSelected(null);
        } else {
          notify(`✅ ${d.updated} Nachricht${d.updated !== 1 ? "en" : ""} aktualisiert`);
        }
        setSelectedIds(new Set());
        fetchMessages();
      }
    } catch { notify("❌ Fehler bei Bulk-Aktion"); }
  };

  const fetchEmails = async () => {
    setFetching(true);
    try {
      const r = await fetch(`${API}/kommunikation/email/fetch`);
      const d = await r.json();
      if (d.fetched > 0) {
        notify(`📧 ${d.fetched} neue E-Mail${d.fetched > 1 ? "s" : ""} abgerufen`);
        fetchMessages();
      } else {
        notify("📭 Keine neuen E-Mails");
      }
    } catch { notify("❌ IMAP-Fehler — Einstellungen prüfen"); }
    setFetching(false);
  };

  useEffect(() => {
    fetchMessages();
    fetch(`${API}/kommunikation/templates`).then(r => r.json()).then(d => setTemplates(d.templates || [])).catch(() => {});
    fetch(`${API}/kommunikation/labels`).then(r => r.json()).then(d => setLabels(d.labels || [])).catch(() => {});
    // Load signature for email
    fetch(`${API}/kommunikation/email/signature`).then(r => r.json()).then(d => setSignature(d.signature || "")).catch(() => {});
  }, [fetchMessages]);

  // Polling every 8 seconds — with AbortController to cancel stale requests
  const pollAbortRef = useRef(null);
  useEffect(() => {
    pollRef.current = setInterval(async () => {
      // Cancel previous pending poll request
      if (pollAbortRef.current) pollAbortRef.current.abort();
      pollAbortRef.current = new AbortController();
      try {
        const r = await fetch(
          `${API}/kommunikation/updates?since=${encodeURIComponent(lastTs.current)}`,
          { signal: pollAbortRef.current.signal }
        );
        const d = await r.json();
        if (d.messages?.length) {
          lastTs.current = new Date().toISOString();
          fetchMessages();
          if (selected && d.messages.some(m => m.thread_id === selected?.thread_id)) {
            fetchThread(selected.id);
          }
        }
      } catch (e) {
        if (e.name === "AbortError") return; // expected — previous request cancelled
      }
    }, 8000);
    return () => {
      clearInterval(pollRef.current);
      if (pollAbortRef.current) pollAbortRef.current.abort();
    };
  }, [selected, fetchMessages, fetchThread]);

  const selectMessage = (msg) => {
    setSelected(msg);
    setReplyText("");
    setAiSuggestion("");
    setSummary("");
    fetchThread(msg.id);
    fetchAiSuggestion(msg);
    // Mark as in_progress if new
    if (msg.status === "new") {
      updateMessage(msg.id, { status: "in_progress" });
    }
    // Load contact
    fetch(`${API}/kommunikation/contacts/${msg.contact_id}`).then(r => r.json()).then(d => setContact(d)).catch(() => {});
  };

  const updateMessage = async (id, data) => {
    try {
      await fetch(`${API}/kommunikation/messages/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data)
      });
      fetchMessages();
      if (selected?.id === id) setSelected(s => ({ ...s, ...data }));
    } catch {}
  };

  const sendReply = async () => {
    if (!replyText.trim() || !selected) return;
    setSending(true);
    try {
      const r = await fetch(`${API}/kommunikation/messages/${selected.id}/reply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          text: replyText,
          sender_id: user?.id || "admin",
          sender_name: user?.name || "Admin",
          ...(selected.channel === "email" && { subject: emailSubject || `Re: ${selected.subject || ""}`, signature })
        })
      });
      const d = await r.json();
      if (d.id || d.ok) {
        setReplyText("");
        setAiSuggestion("");
        fetchThread(selected.id);
        fetchMessages();
        notify("✅ Gesendet");
      } else notify("❌ Fehler beim Senden: " + (d.error || "Unbekannt"));
    } catch { notify("❌ Fehler"); }
    setSending(false);
  };

  const sendNote = async () => {
    if (!internalNote.trim() || !selected) return;
    try {
      await fetch(`${API}/kommunikation/messages/${selected.id}/internal-note`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: internalNote, author_name: user?.name || "Admin", author_id: user?.id || "admin" })
      });
      setInternalNote("");
      fetchThread(selected.id);
      notify("📝 Notiz gespeichert");
    } catch { notify("❌ Fehler"); }
  };

  const handleTranslate = async () => {
    if (!selected) return;
    setTranslating(true);
    try {
      const r = await fetch(`${API}/kommunikation/ai/translate`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: selected.body, target_lang: "Deutsch" })
      });
      const d = await r.json();
      notify(`🌍 ${d.translation || "(leer)"}`);
    } catch {}
    setTranslating(false);
  };

  const handleSummarize = async () => {
    if (!selected) return;
    setSummarizing(true);
    try {
      const r = await fetch(`${API}/kommunikation/ai/summarize`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message_id: selected.id })
      });
      const d = await r.json();
      setSummary(d.summary || "");
    } catch {}
    setSummarizing(false);
  };

  const filtered = messages.filter(m => {
    if (searchQ && !`${m.contact_name} ${m.body} ${m.subject}`.toLowerCase().includes(searchQ.toLowerCase())) return false;
    return true;
  });

  // Sort: pinned first, then urgent, then by date
  const sorted = [...filtered].sort((a, b) => {
    if (a.pinned !== b.pinned) return b.pinned ? 1 : -1;
    if (a.priority !== b.priority) return a.priority === "urgent" ? -1 : 1;
    return new Date(b.created_at) - new Date(a.created_at);
  });

  return (
    <div style={{ display: "flex", height: "100%", overflow: "hidden" }}>
      {/* Left panel — message list */}
      <div style={{ width: 320, flexShrink: 0, borderRight: "1px solid #e5e7eb", display: "flex", flexDirection: "column", background: "#fff" }}>
        {/* Search + fetch button */}
        <div style={{ padding: "12px 16px", borderBottom: "1px solid #e5e7eb" }}>
          <div style={{ display: "flex", gap: 8 }}>
            <input
              value={searchQ}
              onChange={e => setSearchQ(e.target.value)}
              placeholder="🔍 Suchen..."
              style={{ flex: 1, padding: "8px 12px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 13, boxSizing: "border-box", outline: "none" }}
            />
            <button onClick={() => { setBulkMode(b => !b); setSelectedIds(new Set()); }} title="Mehrfachauswahl" style={{
              padding: "8px 10px", border: "1px solid #e5e7eb", borderRadius: 8,
              background: bulkMode ? "#6366f1" : "#fff", color: bulkMode ? "#fff" : "#374151",
              cursor: "pointer", fontSize: 14, flexShrink: 0
            }}>☑️</button>
            {(channel === "email" || channel === "all") && (
              <button onClick={fetchEmails} disabled={fetching} title="E-Mails manuell abrufen" style={{
                padding: "8px 10px", border: "1px solid #e5e7eb", borderRadius: 8,
                background: "#fff", cursor: "pointer", fontSize: 14, flexShrink: 0,
                opacity: fetching ? 0.6 : 1
              }}>{fetching ? "⏳" : "📥"}</button>
            )}
          </div>
        </div>
        {/* Bulk action bar */}
        {bulkMode && (
          <div style={{ padding: "8px 16px", background: selectedIds.size > 0 ? "#eff6ff" : "#f9fafb", borderBottom: "1px solid #e5e7eb", display: "flex", alignItems: "center", gap: 8 }}>
            <input type="checkbox" checked={selectedIds.size === sorted.length && sorted.length > 0}
              onChange={selectAll} style={{ cursor: "pointer" }} />
            <span style={{ fontSize: 12, color: "#374151", flex: 1 }}>
              {selectedIds.size > 0 ? `${selectedIds.size} ausgewählt` : "Alle auswählen"}
            </span>
            {selectedIds.size > 0 && <>
              <button onClick={() => bulkAction("status", "done")} style={{
                padding: "4px 8px", borderRadius: 6, border: "none", background: "#10b981", color: "#fff",
                fontSize: 11, fontWeight: 600, cursor: "pointer"
              }}>✓ Erledigt</button>
              <button onClick={() => bulkAction("status", "in_progress")} style={{
                padding: "4px 8px", borderRadius: 6, border: "none", background: "#f59e0b", color: "#fff",
                fontSize: 11, fontWeight: 600, cursor: "pointer"
              }}>⏳ Bearbeitung</button>
              <button onClick={() => { if (window.confirm(`${selectedIds.size} Nachrichten löschen?`)) bulkAction("delete"); }} style={{
                padding: "4px 8px", borderRadius: 6, border: "none", background: "#ef4444", color: "#fff",
                fontSize: 11, fontWeight: 600, cursor: "pointer"
              }}>🗑️</button>
            </>}
          </div>
        )}
        {/* Status filter */}
        <div style={{ display: "flex", padding: "8px 16px", gap: 6, borderBottom: "1px solid #e5e7eb" }}>
          {["all","new","in_progress","done"].map(s => (
            <button key={s} onClick={() => setFilterStatus(s)} style={{
              padding: "4px 10px", borderRadius: 20, border: "none", cursor: "pointer", fontSize: 11, fontWeight: 600,
              background: filterStatus === s ? "#6366f1" : "#f3f4f6",
              color: filterStatus === s ? "#fff" : "#374151"
            }}>
              {s === "all" ? "Alle" : STATUS_LABELS[s]}
            </button>
          ))}
        </div>
        {/* Message list */}
        <div style={{ overflowY: "auto", flex: 1 }}>
          {sorted.length === 0 && (
            <div style={{ padding: 24, textAlign: "center", color: "#9ca3af", fontSize: 13 }}>
              Keine Nachrichten
            </div>
          )}
          {sorted.map(msg => (
            <div
              key={msg.id}
              onClick={() => bulkMode ? toggleSelect(msg.id, { stopPropagation: () => {} }) : selectMessage(msg)}
              style={{
                padding: "12px 16px", borderBottom: "1px solid #f3f4f6", cursor: "pointer",
                background: selectedIds.has(msg.id) ? "#ede9fe" : selected?.id === msg.id ? "#eff6ff" : msg.priority === "urgent" ? "#fff5f5" : "#fff",
                borderLeft: selectedIds.has(msg.id) ? "3px solid #7c3aed" : selected?.id === msg.id ? "3px solid #6366f1" : msg.priority === "urgent" ? "3px solid #ef4444" : "3px solid transparent",
                transition: "background 0.1s"
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                {bulkMode && (
                  <input type="checkbox" checked={selectedIds.has(msg.id)}
                    onChange={e => toggleSelect(msg.id, e)}
                    onClick={e => e.stopPropagation()}
                    style={{ cursor: "pointer", flexShrink: 0 }} />
                )}
                <Avatar name={msg.contact_name} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontWeight: 700, fontSize: 13, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 130 }}>
                      {msg.contact_name || "Unbekannt"}
                    </span>
                    {msg.pinned && <span title="Gepinnt" style={{ fontSize: 10 }}>📌</span>}
                    {msg.priority === "urgent" && <span style={{ background: "#fef2f2", color: "#ef4444", fontSize: 10, fontWeight: 700, padding: "1px 5px", borderRadius: 4 }}>DRINGEND</span>}
                  </div>
                  <div style={{ fontSize: 11, color: "#6b7280" }}>
                    {CHANNEL_ICONS[msg.channel]} {CHANNEL_LABELS[msg.channel] || msg.channel}
                  </div>
                </div>
                <div style={{ textAlign: "right", flexShrink: 0 }}>
                  <div style={{ fontSize: 10, color: "#9ca3af" }}>{timeAgo(msg.created_at)}</div>
                  <div style={{
                    fontSize: 10, fontWeight: 700, marginTop: 2,
                    color: STATUS_COLORS[msg.status] || "#6b7280"
                  }}>{STATUS_LABELS[msg.status] || msg.status}</div>
                </div>
              </div>
              <div style={{ fontSize: 12, color: "#4b5563", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {msg.subject || msg.body || "(Kein Inhalt)"}
              </div>
              {msg.labels?.length > 0 && (
                <div style={{ display: "flex", gap: 4, marginTop: 4, flexWrap: "wrap" }}>
                  {msg.labels.map(l => (
                    <span key={l} style={{ background: "#ede9fe", color: "#7c3aed", fontSize: 10, padding: "1px 6px", borderRadius: 10 }}>{l}</span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Center panel — conversation */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", background: "#f9fafb" }}>
        {!selected ? (
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "#9ca3af" }}>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>📬</div>
              <div style={{ fontSize: 16, fontWeight: 600 }}>Nachricht auswählen</div>
              <div style={{ fontSize: 13 }}>Wähle eine Konversation aus der Liste</div>
            </div>
          </div>
        ) : (
          <>
            {/* Header */}
            <div style={{
              padding: "12px 20px", background: "#fff", borderBottom: "1px solid #e5e7eb",
              display: "flex", alignItems: "center", gap: 12
            }}>
              <Avatar name={selected.contact_name} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 15, color: "#111827" }}>{selected.contact_name || "Unbekannt"}</div>
                <div style={{ fontSize: 12, color: "#6b7280" }}>
                  {CHANNEL_ICONS[selected.channel]} {CHANNEL_LABELS[selected.channel]} · {selected.contact_identifier}
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                {/* Status dropdown */}
                <select
                  value={selected.status}
                  onChange={e => updateMessage(selected.id, { status: e.target.value })}
                  style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 12, background: "#fff", cursor: "pointer" }}
                >
                  <option value="new">🔴 Neu</option>
                  <option value="in_progress">🟡 In Bearbeitung</option>
                  <option value="done">🟢 Erledigt</option>
                </select>
                {/* Priority */}
                <button onClick={() => updateMessage(selected.id, { priority: selected.priority === "urgent" ? "normal" : "urgent" })} style={{
                  padding: "6px 10px", borderRadius: 8, border: "1px solid #e5e7eb", background: "#fff",
                  cursor: "pointer", fontSize: 12, color: selected.priority === "urgent" ? "#ef4444" : "#6b7280"
                }}>
                  {selected.priority === "urgent" ? "⚡ Dringend" : "Normal"}
                </button>
                {/* Pin */}
                <button onClick={() => updateMessage(selected.id, { pinned: !selected.pinned })} style={{
                  padding: "6px 10px", borderRadius: 8, border: "1px solid #e5e7eb", background: "#fff",
                  cursor: "pointer", fontSize: 14
                }} title={selected.pinned ? "Losgelöst" : "Pinnen"}>
                  {selected.pinned ? "📌" : "📍"}
                </button>
                {/* Translate */}
                <button onClick={handleTranslate} disabled={translating} style={{
                  padding: "6px 10px", borderRadius: 8, border: "1px solid #e5e7eb", background: "#fff",
                  cursor: "pointer", fontSize: 12, color: "#6b7280"
                }}>🌍</button>
                {/* Summarize */}
                <button onClick={handleSummarize} disabled={summarizing} style={{
                  padding: "6px 10px", borderRadius: 8, border: "1px solid #e5e7eb", background: "#fff",
                  cursor: "pointer", fontSize: 12, color: "#6b7280"
                }}>📋</button>
              </div>
            </div>

            {/* Summary banner */}
            {summary && (
              <div style={{ padding: "8px 20px", background: "#eff6ff", borderBottom: "1px solid #bfdbfe", fontSize: 13, color: "#1e40af" }}>
                📋 <strong>Zusammenfassung:</strong> {summary}
                <button onClick={() => setSummary("")} style={{ marginLeft: 8, background: "none", border: "none", cursor: "pointer", color: "#6b7280" }}>✕</button>
              </div>
            )}

            {/* Thread */}
            <div style={{ flex: 1, overflowY: "auto", padding: 20, display: "flex", flexDirection: "column", gap: 12 }}>
              {thread.map((m, i) => {
                const isOut = m.direction === "out";
                const isNote = m.type === "internal_note";
                return (
                  <div key={m.id || i} style={{
                    display: "flex", flexDirection: "column",
                    alignItems: isNote ? "center" : isOut ? "flex-end" : "flex-start"
                  }}>
                    {isNote ? (
                      <div style={{
                        background: "#fef9c3", border: "1px solid #fde68a", borderRadius: 8,
                        padding: "8px 14px", fontSize: 12, color: "#78350f", maxWidth: "70%"
                      }}>
                        🔒 <strong>{m.by_name || "Team"}</strong>: {m.note}
                        <span style={{ color: "#9ca3af", marginLeft: 8 }}>{timeAgo(m.created_at)}</span>
                      </div>
                    ) : (
                      <>
                        <div style={{
                          background: isOut ? "#6366f1" : "#fff",
                          color: isOut ? "#fff" : "#111827",
                          borderRadius: isOut ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                          padding: "10px 14px", maxWidth: "70%",
                          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                          border: isOut ? "none" : "1px solid #e5e7eb",
                          fontSize: 14, lineHeight: 1.5
                        }}>
                          {m.subject && <div style={{ fontWeight: 700, marginBottom: 4, fontSize: 13 }}>{m.subject}</div>}
                          <EmailBody
                            body={m.body || ""}
                            isHtml={!!(m.body && m.body.includes("<") && m.body.includes(">") && (m.body.includes("</") || m.body.includes("/>")))}
                          />
                        </div>
                        <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>
                          {isOut ? `✓ ${m.sent_by_name || "Team"} · ` : ""}{timeAgo(m.created_at)}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
              <div ref={threadEndRef} />
            </div>

            {/* Reply box */}
            <div style={{ background: "#fff", borderTop: "1px solid #e5e7eb", padding: 16 }}>
              {/* KI Suggestion — always visible */}
              {(aiLoading || aiSuggestion) && (
                <div style={{
                  background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 8,
                  padding: "10px 14px", marginBottom: 10, fontSize: 13
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: aiSuggestion ? 6 : 0 }}>
                    <span style={{ color: "#3b82f6", fontWeight: 700, fontSize: 12 }}>🤖 KI-Vorschlag</span>
                    {aiLoading && <span style={{ color: "#9ca3af", fontSize: 11 }}>wird erstellt...</span>}
                  </div>
                  {aiSuggestion && (
                    <>
                      <div style={{ color: "#1e3a5f", lineHeight: 1.5 }}>{aiSuggestion}</div>
                      <button onClick={() => setReplyText(aiSuggestion)} style={{
                        marginTop: 8, padding: "4px 12px", background: "#3b82f6", color: "#fff",
                        border: "none", borderRadius: 6, fontSize: 12, cursor: "pointer", fontWeight: 600
                      }}>↑ Übernehmen</button>
                    </>
                  )}
                </div>
              )}

              {/* Reply / Internal tabs */}
              <div style={{ display: "flex", gap: 4, marginBottom: 10 }}>
                {["reply","internal"].map(p => (
                  <button key={p} onClick={() => setActivePane(p)} style={{
                    padding: "6px 14px", borderRadius: 8, border: "none", cursor: "pointer", fontSize: 12, fontWeight: 600,
                    background: activePane === p ? "#6366f1" : "#f3f4f6",
                    color: activePane === p ? "#fff" : "#374151"
                  }}>
                    {p === "reply" ? "↩ Antwort" : "🔒 Interne Notiz"}
                  </button>
                ))}
                {/* Templates */}
                {templates.length > 0 && (
                  <select onChange={e => { if (e.target.value) setReplyText(e.target.value); e.target.value = ""; }}
                    style={{ marginLeft: "auto", padding: "6px 10px", borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 12, background: "#fff" }}>
                    <option value="">📝 Textbaustein...</option>
                    {templates.map(t => <option key={t.id} value={t.body}>{t.name}</option>)}
                  </select>
                )}
              </div>

              {activePane === "reply" ? (
                <>
                  {/* E-Mail specific fields */}
                  {selected.channel === "email" && (
                    <div style={{ marginBottom: 8 }}>
                      <input
                        value={emailSubject}
                        onChange={e => setEmailSubject(e.target.value)}
                        placeholder={`Betreff: Re: ${selected.subject || ""}`}
                        style={{ width: "100%", padding: "8px 12px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 13, boxSizing: "border-box", outline: "none", marginBottom: 6 }}
                      />
                    </div>
                  )}
                  <textarea
                    value={replyText}
                    onChange={e => setReplyText(e.target.value)}
                    placeholder={`Antwort via ${CHANNEL_LABELS[selected.channel] || selected.channel}...`}
                    onKeyDown={e => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) sendReply(); }}
                    rows={4}
                    style={{
                      width: "100%", padding: "10px 14px", border: "1px solid #e5e7eb", borderRadius: 8,
                      fontSize: 14, resize: "vertical", boxSizing: "border-box", outline: "none",
                      fontFamily: "inherit", lineHeight: 1.5
                    }}
                  />
                  {/* Signature preview for email — compact */}
                  {selected.channel === "email" && signature && (
                    <div style={{ marginTop: 4, fontSize: 11, color: "#9ca3af" }}>
                      ✍️ Signatur von <strong>{user?.name || "dir"}</strong> wird angehängt
                    </div>
                  )}
                  <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
                    <button onClick={sendReply} disabled={sending || !replyText.trim()} style={{
                      padding: "8px 20px", background: "#6366f1", color: "#fff", border: "none",
                      borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer",
                      opacity: (sending || !replyText.trim()) ? 0.5 : 1
                    }}>
                      {sending ? "Sende..." : `Senden ↗`}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <textarea
                    value={internalNote}
                    onChange={e => setInternalNote(e.target.value)}
                    placeholder="Interne Notiz für das Team (nicht für Kunden sichtbar)..."
                    rows={3}
                    style={{
                      width: "100%", padding: "10px 14px", border: "1px solid #fde68a", borderRadius: 8,
                      fontSize: 14, resize: "vertical", boxSizing: "border-box", outline: "none",
                      fontFamily: "inherit", background: "#fefce8", lineHeight: 1.5
                    }}
                  />
                  <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
                    <button onClick={sendNote} disabled={!internalNote.trim()} style={{
                      padding: "8px 20px", background: "#f59e0b", color: "#fff", border: "none",
                      borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer",
                      opacity: !internalNote.trim() ? 0.5 : 1
                    }}>📝 Notiz speichern</button>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>

      {/* Right panel — contact info */}
      {selected && (
        <div style={{
          width: 260, flexShrink: 0, borderLeft: "1px solid #e5e7eb", background: "#fff",
          overflowY: "auto", padding: 16
        }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: "#374151", marginBottom: 12 }}>👤 Kontakt</div>
          <div style={{ fontSize: 13, marginBottom: 8 }}>
            <div style={{ color: "#6b7280", fontSize: 11 }}>Name</div>
            <div style={{ fontWeight: 600 }}>{selected.contact_name || "Unbekannt"}</div>
          </div>
          <div style={{ fontSize: 13, marginBottom: 12 }}>
            <div style={{ color: "#6b7280", fontSize: 11 }}>E-Mail / ID</div>
            <div style={{ fontSize: 12, wordBreak: "break-all" }}>{selected.contact_identifier}</div>
          </div>
          {/* Editable contact notes */}
          <ContactNotes contactId={selected.contact_id} key={selected.contact_id} />
          {contact?.ai_memory && (
            <div style={{ fontSize: 13, marginBottom: 8 }}>
              <div style={{ color: "#6b7280", fontSize: 11, marginBottom: 2 }}>🧠 KI-Gedächtnis</div>
              <div style={{ background: "#eff6ff", borderRadius: 6, padding: 8, fontSize: 12 }}>{contact.ai_memory}</div>
            </div>
          )}

          <div style={{ height: 1, background: "#e5e7eb", margin: "16px 0" }} />

          <div style={{ fontWeight: 700, fontSize: 13, color: "#374151", marginBottom: 12 }}>🏷️ Labels</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
            {(selected.labels || []).map(l => (
              <span key={l} style={{
                background: "#ede9fe", color: "#7c3aed", fontSize: 11,
                padding: "2px 8px", borderRadius: 10, cursor: "pointer"
              }} onClick={() => updateMessage(selected.id, { labels: (selected.labels || []).filter(x => x !== l) })}>
                {l} ✕
              </span>
            ))}
          </div>
          {labels.filter(l => !(selected.labels || []).includes(l.name)).map(l => (
            <button key={l.id} onClick={() => updateMessage(selected.id, { labels: [...(selected.labels || []), l.name] })} style={{
              background: "#f3f4f6", border: "none", borderRadius: 10,
              fontSize: 11, padding: "2px 8px", cursor: "pointer", marginRight: 4, marginBottom: 4
            }}>+ {l.name}</button>
          ))}

          <div style={{ height: 1, background: "#e5e7eb", margin: "16px 0" }} />

          <div style={{ fontWeight: 700, fontSize: 13, color: "#374151", marginBottom: 8 }}>📊 Ticket-Info</div>
          <div style={{ fontSize: 12, color: "#6b7280" }}>
            <div>Erstellt: {parseUtc(selected.created_at).toLocaleString("de-DE")}</div>
            <div>Kanal: {CHANNEL_LABELS[selected.channel] || selected.channel}</div>
            <div>Kategorie: {selected.category || "–"}</div>
            {selected.assigned_name && <div>Zugewiesen: {selected.assigned_name}</div>}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Hauptkomponente ──────────────────────────────────────────────────────────
export default function KommunikationView({ user, notify }) {
  const TABS = [
    { id: "inbox",        label: "📥 Inbox",      channel: "all" },
    { id: "whatsapp",     label: "💬 WhatsApp",   channel: "whatsapp" },
    { id: "email",        label: "📧 E-Mail",     channel: "email" },
    { id: "instagram",    label: "📸 Instagram",  channel: "instagram" },
    { id: "tiktok",       label: "🎵 TikTok",     channel: "tiktok" },
    { id: "facebook",     label: "📘 Facebook",   channel: "facebook" },
    { id: "webchat",      label: "🌐 Web-Chat",   channel: "webchat" },
    { id: "einstellungen",label: "⚙️ Einstellungen", channel: null },
  ];

  const [tab, setTab] = useState("inbox");
  const active = TABS.find(t => t.id === tab);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", background: "#f9fafb", overflow: "hidden" }}>
      {/* Sub-tab bar */}
      <div style={{
        display: "flex", alignItems: "center", gap: 2, padding: "8px 16px",
        background: "#fff", borderBottom: "1px solid #e5e7eb", overflowX: "auto", flexShrink: 0
      }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: "7px 14px", borderRadius: 8, border: "none", cursor: "pointer",
            fontSize: 13, fontWeight: tab === t.id ? 700 : 500, whiteSpace: "nowrap",
            background: tab === t.id ? "#ede9fe" : "transparent",
            color: tab === t.id ? "#7c3aed" : "#374151",
            transition: "all 0.15s"
          }}>{t.label}</button>
        ))}
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        {tab === "einstellungen" ? (
          <EinstellungenTab notify={notify} />
        ) : (
          <InboxView channel={active?.channel || "all"} notify={notify} user={user} />
        )}
      </div>
    </div>
  );
}
